import { mapPagingToRecordInfo } from './authHelpers';

export function mapLocationCountryRow(row) {
    if (!row) {
        return row;
    }
    return {
        ...row,
        id: row.id != null ? row.id : row.countryId,
        label: row.label != null ? row.label : row.name,
        code: row.code != null ? row.code : row.countryId,
    };
}

export function mapLocationProvinceRow(row) {
    if (!row) {
        return row;
    }
    return {
        ...row,
        id: row.id != null ? row.id : row.provinceId,
        label: row.label != null ? row.label : row.name,
    };
}

export function mapLocationCityRow(row) {
    if (!row) {
        return row;
    }
    return {
        ...row,
        id: row.id != null ? row.id : row.cityId,
        label: row.label != null ? row.label : row.name,
    };
}

export function toLegacyRecordInfo(paging) {
    const info = mapPagingToRecordInfo(paging);
    return {
        totalRecords: info.totalRecords,
        page: info.page,
        nrecords: info.nrecords,
    };
}

export function legacyStoreSearchPayload(data) {
    const rows = Array.isArray(data.body) ? data.body : [];
    return {
        stores: rows,
        recordInfo: toLegacyRecordInfo(data.paging),
    };
}

export function legacyStoreViewPayload(data) {
    const store = data.body || {};
    return { store };
}

export function legacyMerchantSearchList(data) {
    return Array.isArray(data.body) ? data.body : [];
}

export function legacyCurrencyPayload(data) {
    const body = data.body || {};
    const selected = body.selected || {};
    return {
        listCurrency: body.options || body.listCurrency || [],
        configured: body.configured,
        selected,
        lookupDtlId: selected.lookupDtlId,
        lookupHdrId: selected.lookupHdrId,
        lookupCode: selected.lookupCode,
        lookupValue: selected.lookupValue,
        description: selected.description,
        icon: selected.icon,
    };
}

export function legacyFirstLoginPayload(data) {
    const body = data.body || data;
    const firstLogin = body.firstLogin === true || body.isFirstLogin === 1;
    return {
        ...body,
        isFirstLogin: firstLogin ? 1 : 0,
        firstLogin,
    };
}

export function resolveMerchantSearchMode(params) {
    if (!params) {
        return 'ALL';
    }
    const reportPartner = params.isReportPartner;
    if (reportPartner === -1 || reportPartner === '-1') {
        return 'REPORT_PARTNER';
    }
    const selected = params.isSelected;
    const exchange = params.isExchange;
    const consolidate = params.isMerchantConsolidate;
    if (
        (selected === -1 || selected === '-1') &&
        (exchange === -1 || exchange === '-1')
    ) {
        return 'EXCHANGE';
    }
    if (
        (selected === -1 || selected === '-1') &&
        (consolidate === -1 || consolidate === '-1')
    ) {
        return 'CONSOLIDATE';
    }
    return 'ALL';
}

/** PUT idp/v2/merchant/{id} — additionalFields shape (DEV success curl). */
export function buildMerchantUpdateAdditionalFields(fields) {
    if (!Array.isArray(fields)) {
        return [];
    }
    return fields.map((item, index) => {
        const row = {
            fieldId: item.fieldId,
            fieldName: item.fieldName,
            fieldType: item.fieldType,
            status: item.status,
            showCard: item.showCard,
            value: item.value,
            key: item.key != null ? item.key : index,
        };
        if (item.fieldTypeStr != null) {
            row.fieldTypeStr = item.fieldTypeStr;
        }
        if (item.createdDate != null) {
            row.createdDate = item.createdDate;
        }
        if (item.lastUpdatedDate != null) {
            row.lastUpdatedDate = item.lastUpdatedDate;
        }
        return row;
    });
}

/** Exclude current merchant; empty array when none selected (DEV success). */
export function buildMerchantIdConsolidatePayload(listMerchantConsolidation, currentMerchantId) {
    const ids = [];
    (listMerchantConsolidation || []).forEach((item) => {
        const id = item && item.merchantId;
        if (id && id !== currentMerchantId) {
            ids.push(id);
        }
    });
    return ids;
}

export function mapHoldingMerchants(merchant) {
    if (!merchant) {
        return [];
    }
    const holdingGroup = merchant.holdingGroup || {};
    const holdingMerchant = holdingGroup.holdingMerchant || merchant.holdingMerchant;
    const subsidiaries = holdingGroup.subsidiaries || merchant.subsidiaries || [];

    const list = [];
    if (holdingMerchant && (holdingMerchant.merchantName || holdingMerchant.merchantId || holdingMerchant.merchantCode)) {
        list.push({
            key: holdingMerchant.merchantId || 'holding-leader',
            merchantId: holdingMerchant.merchantId || '-',
            merchantCode: holdingMerchant.merchantCode || '-',
            merchantName: holdingMerchant.merchantName || '-',
        });
    }

    if (Array.isArray(subsidiaries)) {
        subsidiaries.forEach((sub, idx) => {
            if (sub && (sub.merchantName || sub.merchantId || sub.merchantCode)) {
                list.push({
                    key: sub.merchantId || `subsidiary-${idx}`,
                    merchantId: sub.merchantId || '-',
                    merchantCode: sub.merchantCode || '-',
                    merchantName: sub.merchantName || '-',
                });
            }
        });
    }

    return list;
}

export function mapExchangePartners(merchant) {
    if (!merchant) {
        return [];
    }
    const exchangeGroups = Array.isArray(merchant.exchangeGroups) ? merchant.exchangeGroups : [];
    const list = [];

    exchangeGroups.forEach((group, gIdx) => {
        const partners = group && Array.isArray(group.partners) ? group.partners : [];
        const groupCode = (group && group.exchangeGroupCode) || '-';
        const groupId = (group && group.exchangeGroupId) || gIdx;

        partners.forEach((partner, pIdx) => {
            if (partner && (partner.name || partner.merchantName || partner.referenceId || partner.merchantId)) {
                list.push({
                    key: partner.referenceId || `${groupId}-${pIdx}`,
                    merchantId: partner.referenceId || partner.merchantId || '-',
                    merchantCode: partner.merchantCode || partner.code || groupCode,
                    merchantName: partner.name || partner.merchantName || '-',
                });
            }
        });
    });

    return list;
}

export function buildMerchantUpdateRequestBody(data) {
    if (!data) {
        return {};
    }
    return {
        merchantName: data.merchantName,
        merchantWebsite: data.merchantWebsite,
        merchantLogo: data.merchantLogo,
        isRequiredVerification: data.isRequiredVerification,
        isRedeemOtp: data.isRedeemOtp,
        issuingReferral: data.issuingReferral,
        paramCurrencyPoint: data.paramCurrencyPoint,
        currencyId: data.currencyId,
        lookupDtlId: data.lookupDtlId,
        address: data.address,
        contact: data.contact,
        additionalFields: buildMerchantUpdateAdditionalFields(data.additionalFields),
        isApproval: data.isApproval,
        isAdditionalFields: data.isAdditionalFields,
        merchantIdConsolidate: Array.isArray(data.merchantIdConsolidate)
            ? data.merchantIdConsolidate
            : [],
    };
}
export function legacyRegistrationOptionsPayload(data) {
    const body = (data && data.body) || data || {};
    return {
        listIndustry: body.listIndustry || [],
        listNumberCustomer: body.listNumberCustomer || [],
        trxFreqMonthly: body.trxFreqMonthly || [],
        totalTrxFreqMonthly: body.totalTrxFreqMonthly || [],
    };
}

function buildRegistrationBody({
    email,
    phoneNumber,
    firstName,
    lastName,
    merchantName,
    merchantType,
    password,
    address,
    countryId,
    cityId,
    stateProvinceId,
    typeOfIndustry,
    numberOfCustomer,
    monthlyTransactionFrequency,
    monthlyTransactionAmount,
    postalCode,
}) {
    return {
        email,
        phoneNumber,
        firstName,
        lastName,
        merchantName,
        merchantType,
        password,
        address,
        countryId,
        cityId,
        stateProvinceId,
        postalCode,
        typeOfIndustry,
        numberOfCustomer,
        monthlyTransactionFrequency,
        monthlyTransactionAmount,
    };
}

export { buildRegistrationBody };
